"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { formatPrice } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { addToCart } from "@/lib/cart";

// "장바구니에 담았습니다" 안내를 몇 초 후 자동으로 닫는다.
const ADD_TO_CART_CONFIRMATION_MILLISECONDS = 3000;

interface OptionView {
  id: string;
  label: string;
  price: number;
  finalPrice: number;
  stock: number;
  isAvailable: boolean;
}

interface Props {
  productId: string;
  productName: string;
  productUnit: string;
  price: number;
  finalPrice: number;
  isOnDiscount: boolean;
  discountRate: number;
  isPreorder: boolean;
  isAvailable: boolean;
  effectiveStock: number;
  minOrderQuantity: number;
  options: OptionView[];
}

// 상세 페이지의 구매 영역(가격 + 단위 선택 + 수량 + 주문 버튼).
// 옵션 상품은 여기서 단위를 고르면 가격/재고가 즉시 반영되고, 고른 옵션이
// /order 로 그대로 전달된다.
export function ProductPurchasePanel({
  productId,
  productName,
  productUnit,
  price,
  finalPrice,
  isOnDiscount,
  discountRate,
  isPreorder,
  isAvailable,
  effectiveStock,
  minOrderQuantity,
  options,
}: Props) {
  const router = useRouter();
  const hasOptions = options.length > 0;

  // 상세 페이지 진입 시 1회만 발생 (productId가 바뀌면 다시 발생).
  const viewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (viewedRef.current === productId) return;
    viewedRef.current = productId;
    track("product_viewed", { product_name: productName, price: finalPrice });
  }, [productId, productName, finalPrice]);

  // 옵션이 2개 이상일 때만 선택 드롭다운을 보여준다(1개면 단일 단위).
  const hasMultipleOptions = options.length >= 2;

  const firstSellable =
    options.find((o) => o.isAvailable && (isPreorder || o.stock > 0)) ??
    options.find((o) => o.isAvailable) ??
    null;
  const [selectedOptionId, setSelectedOptionId] = useState<string>(
    () => firstSellable?.id ?? "",
  );
  const [qty, setQty] = useState(minOrderQuantity);
  const [navigating, setNavigating] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const addedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (addedTimeoutRef.current) clearTimeout(addedTimeoutRef.current);
    };
  }, []);

  const selectedOption = options.find((o) => o.id === selectedOptionId) ?? null;

  const unitPrice = hasOptions ? (selectedOption?.finalPrice ?? 0) : finalPrice;
  const unitOriginal = hasOptions ? (selectedOption?.price ?? 0) : price;
  const unitLabel = hasOptions ? (selectedOption?.label ?? "") : productUnit;

  const stockCap = isPreorder
    ? 99
    : hasOptions
      ? (selectedOption?.stock ?? 0)
      : effectiveStock;

  const anySellable = hasOptions
    ? options.some((o) => o.isAvailable && (isPreorder || o.stock > 0))
    : isPreorder || effectiveStock > 0;
  const soldOut = !isAvailable || !anySellable;
  const selectedSoldOut =
    !soldOut && hasOptions && selectedOption
      ? !selectedOption.isAvailable ||
        (!isPreorder && selectedOption.stock <= 0)
      : false;

  // 옵션 변경으로 재고 상한이 바뀌면 표시 수량을 유효 범위(최소 주문
  // 수량~재고)로 맞춘다.
  // (effect + setState 대신 파생값으로 계산해 불필요한 재렌더를 막는다.)
  const effectiveQty = Math.min(
    Math.max(minOrderQuantity, qty),
    Math.max(minOrderQuantity, stockCap),
  );

  function order() {
    if (soldOut || selectedSoldOut) return;
    setNavigating(true);
    const params = new URLSearchParams({
      productId,
      qty: String(effectiveQty),
    });
    if (hasOptions && selectedOptionId)
      params.set("optionId", selectedOptionId);
    if (isPreorder) params.set("preorder", "1");
    router.push(`/order?${params.toString()}`);
  }

  function handleAddToCart() {
    if (soldOut || selectedSoldOut) return;
    addToCart(
      productId,
      hasOptions ? selectedOptionId || null : null,
      effectiveQty,
    );
    setJustAdded(true);
    if (addedTimeoutRef.current) clearTimeout(addedTimeoutRef.current);
    addedTimeoutRef.current = setTimeout(
      () => setJustAdded(false),
      ADD_TO_CART_CONFIRMATION_MILLISECONDS,
    );
  }

  const stepButton =
    "w-12 h-12 flex items-center justify-center rounded-lg border-2 border-(--color-border) text-xl font-bold text-primary transition-colors hover:border-(--color-primary) disabled:opacity-30 disabled:cursor-not-allowed";

  return (
    <>
      {/* 가격 */}
      <div className="mt-6 p-5 bg-cream rounded-xl border border-(--color-border)">
        {isOnDiscount ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-muted line-through">
                정가 {formatPrice(unitOriginal)}
              </span>
              <span className="badge badge-gold text-xs">{discountRate}%</span>
            </div>
            <div className="text-3xl font-bold text-primary">
              {formatPrice(unitPrice)}
            </div>
          </div>
        ) : (
          <div className="text-3xl font-bold text-primary">
            {formatPrice(unitPrice)}
          </div>
        )}
        <p className="text-sm text-text-light mt-1">
          {hasOptions ? `단위: ${unitLabel}` : unitLabel}
        </p>
      </div>

      {soldOut ? (
        <div className="mt-6">
          <div className="w-full py-3 text-center rounded-lg bg-(--color-cream-dark) text-text-muted font-semibold">
            현재 품절된 상품입니다
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {/* 단위 선택 (옵션 2개 이상일 때만) */}
          {hasMultipleOptions && (
            <div>
              <label className="label" htmlFor="option-select">
                단위 선택
              </label>
              <select
                id="option-select"
                className="input"
                value={selectedOptionId}
                onChange={(e) => setSelectedOptionId(e.target.value)}
              >
                {options.map((o) => {
                  const oSoldOut = !isPreorder && o.stock <= 0;
                  return (
                    <option
                      key={o.id}
                      value={o.id}
                      disabled={!o.isAvailable || oSoldOut}
                    >
                      {o.label} · {formatPrice(o.finalPrice)}
                      {!o.isAvailable
                        ? " (판매중지)"
                        : oSoldOut
                          ? " (품절)"
                          : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* 수량 */}
          <div>
            <label className="label">수량</label>
            {minOrderQuantity > 1 && (
              <p className="text-sm text-text-light mb-1.5">
                최소 {minOrderQuantity}개부터 주문 가능
              </p>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() =>
                  setQty(Math.max(minOrderQuantity, effectiveQty - 1))
                }
                disabled={effectiveQty <= minOrderQuantity}
                className={stepButton}
                aria-label="수량 감소"
              >
                −
              </button>
              <span
                aria-live="polite"
                aria-atomic="true"
                className="w-14 text-center text-xl font-bold text-text-dark"
              >
                {effectiveQty}
              </span>
              <button
                type="button"
                onClick={() => setQty(Math.min(stockCap, effectiveQty + 1))}
                disabled={effectiveQty >= stockCap}
                className={stepButton}
                aria-label="수량 증가"
              >
                +
              </button>
              {!isPreorder && stockCap > 0 && stockCap <= 10 && (
                <span className="text-sm font-semibold text-warning">
                  ⚡ 매진 임박
                </span>
              )}
            </div>
          </div>

          {/* 합계 */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-light">합계</span>
            <span className="text-xl font-bold text-primary">
              {formatPrice(unitPrice * effectiveQty)}
            </span>
          </div>

          {selectedSoldOut && (
            <p className="text-sm text-error">
              선택하신 단위는 품절입니다. 다른 단위를 선택해 주세요.
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={navigating || selectedSoldOut}
              className="btn-outline flex-1 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              장바구니 담기
            </button>
            <button
              type="button"
              onClick={order}
              disabled={navigating || selectedSoldOut}
              className={`${isPreorder ? "btn-outline" : "btn-primary"} flex-[1.2] disabled:opacity-70 disabled:cursor-wait flex items-center justify-center gap-2`}
            >
              {navigating && (
                <span
                  className="inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
                  aria-hidden
                />
              )}
              {navigating
                ? "이동 중..."
                : isPreorder
                  ? "예약 주문하기"
                  : "주문하기"}
            </button>
          </div>

          {justAdded && (
            <div
              role="status"
              className="flex items-center justify-between gap-3 text-sm px-4 py-3 rounded-lg"
              style={{
                backgroundColor: "#DCFCE7",
                color: "var(--color-success)",
              }}
            >
              <span>장바구니에 담았습니다</span>
              <Link href="/cart" className="font-semibold underline shrink-0">
                장바구니 보기
              </Link>
            </div>
          )}
        </div>
      )}
    </>
  );
}
